import { Component, effect, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '@auth0/auth0-angular';
import { WorkspaceService, ApiService } from '@sentry/forms-core';
import { AppInsightsService } from '@sentry/forms-core';
import { ImagePreviewComponent } from '@sentry/media';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    ImagePreviewComponent,
  ],
  template: `
    <div class="flex flex-col h-[100dvh] bg-background overflow-hidden" style="padding-bottom: env(safe-area-inset-bottom)">
      <!-- Top nav bar -->
      <nav class="flex-shrink-0 flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 border-b border-outline-variant z-10 bg-background text-on-background">
        <a routerLink="/" class="flex items-center gap-2 relative z-10 no-underline shrink-0" style="text-decoration:none">
          <img src="assets/central_app_icon.png" style="width:30px;height:30px;object-fit:contain" alt="Mining Sentry" />
          <span class="hidden sm:inline" style="font-family:'Anton',sans-serif;font-size:1.25rem;letter-spacing:0.05em">
            <span style="color:#ffc500">Mining</span><span style="color:#000"> Sentry</span>
          </span>
        </a>
        @if (!isLoading() && isAuthenticated() && !isSelectWorkspacePage()) {
          <a
            routerLink="/"
            routerLinkActive="font-semibold"
            [routerLinkActiveOptions]="{ exact: true }"
            mat-button style="color:inherit"
          >
            <mat-icon>add_circle_outline</mat-icon>
            <span class="hidden sm:inline">Report</span>
          </a>
          <a
            routerLink="/incidents"
            routerLinkActive="font-semibold"
            mat-button style="color:inherit"
          >
            <mat-icon>warning_amber</mat-icon>
            <span class="hidden sm:inline">Incidents</span>
          </a>
          <a
            routerLink="/chat"
            routerLinkActive="font-semibold"
            mat-button style="color:inherit"
          >
            <mat-icon>smart_toy</mat-icon>
            <span class="hidden sm:inline">Chat</span>
          </a>
        }

        <span class="flex-1"></span>

        @if (activeWorkspace(); as ws) {
          @if (!isSelectWorkspacePage()) {
            <span class="hidden sm:flex items-center gap-1 text-sm opacity-60 shrink-0">
              <mat-icon style="font-size:16px;width:16px;height:16px">business</mat-icon>
              {{ ws.name }}
            </span>
          }
        }

        @if (!isLoading() && isAuthenticated()) {
          @if (!isSelectWorkspacePage()) {
            <a mat-icon-button matTooltip="Forms" routerLink="/forms">
              <mat-icon>settings</mat-icon>
            </a>
          }
          <button color="accent" mat-icon-button [matMenuTriggerFor]="userMenu">
            <mat-icon>account_circle</mat-icon>
          </button>
          <mat-menu #userMenu="matMenu">
            @if (currentUser(); as u) {
              <button mat-menu-item disabled>
                <mat-icon>person</mat-icon>
                {{ u.firstName }} {{ u.lastName }}
              </button>
            } @else if (user()?.email) {
              <button mat-menu-item disabled>
                <mat-icon>person</mat-icon>
                {{ user()!.email }}
              </button>
            }
            @if (activeWorkspace(); as ws) {
              <button mat-menu-item disabled class="sm:!hidden">
                <mat-icon>business</mat-icon>
                {{ ws.name }}
              </button>
            }
            @if (canSwitchWorkspace() > 1) {
              <button mat-menu-item (click)="switchWorkspace()">
                <mat-icon>swap_horiz</mat-icon>
                Change Workspace
              </button>
            }
            <button mat-menu-item (click)="logout()">
              <mat-icon>logout</mat-icon>
              Logout
            </button>
          </mat-menu>
        }
      </nav>

      <!-- Page content -->
      @if (isLoading() || syncInProgress()) {
        <div class="flex flex-1 items-center justify-center">
          <mat-spinner></mat-spinner>
        </div>
      } @else if (isAuthenticated() && accessDenied()) {
        <div class="flex flex-1 flex-col items-center justify-center gap-4 text-center px-6">
          <mat-icon style="font-size:64px;width:64px;height:64px;color:var(--mat-sys-error)">block</mat-icon>
          <p class="text-xl font-medium">Access not allowed</p>
          <p class="opacity-60">Your account is not authorized to access this application. Please contact support.</p>
          <button mat-stroked-button (click)="logout()">
            <mat-icon>logout</mat-icon>
            Sign out
          </button>
        </div>
      } @else if (isAuthenticated()) {
        <div class="flex-1 min-h-0 flex flex-col">
          <router-outlet />
        </div>
      } @else {
        <div class="flex flex-1 flex-col items-center justify-center gap-6">
          <mat-icon style="font-size:64px;width:64px;height:64px;opacity:0.3">lock</mat-icon>
          <p class="text-on-surface opacity-60 text-lg">Sign in to access the form designer</p>
          <button mat-flat-button (click)="login()">
            <mat-icon>login</mat-icon>
            Sign in
          </button>
        </div>
      }
    </div>
    <sentry-image-preview />
  `,
})
export class AppComponent {
  private authService = inject(AuthService);
  private workspaceService = inject(WorkspaceService);
  private apiService = inject(ApiService);
  private appInsights = inject(AppInsightsService);
  private router = inject(Router);

  isLoading = toSignal(this.authService.isLoading$, { initialValue: true });
  isAuthenticated = toSignal(this.authService.isAuthenticated$, { initialValue: false });
  user = toSignal(this.authService.user$, { initialValue: null });

  activeWorkspace = this.workspaceService.activeWorkspace;
  currentUser = this.workspaceService.currentUser;
  canSwitchWorkspace = this.workspaceService.workspaceCount;
  accessDenied   = signal(false);
  syncInProgress = signal(false);

  isSelectWorkspacePage = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects.startsWith('/select-workspace')),
      startWith(this.router.url.startsWith('/select-workspace')),
    ),
    { initialValue: false },
  );

  private userSynced = false;
  private pendingReturnUrl: string | null = null;
  private pendingWorkspaceId: string | null = null;

  constructor() {
    // Capture the return URL from Auth0 appState after redirect-back.
    this.authService.appState$.pipe(takeUntilDestroyed()).subscribe((appState) => {
      if (appState?.target) this.pendingReturnUrl = appState.target;
      if (appState?.workspaceId) this.pendingWorkspaceId = appState.workspaceId;
    });

    // Any Auth0 error (expired session, silent auth failure, invalid request, etc.)
    // means we cannot trust the current session — clear it and redirect to login immediately.
    this.authService.error$.pipe(takeUntilDestroyed()).subscribe(() => {
      const workspaceId = this.workspaceService.activeWorkspace()?.id ?? null;
      this.workspaceService.clearSession();
      this.syncInProgress.set(false);
      this.authService.loginWithRedirect({ appState: { target: this.router.url, workspaceId } });
    });

    // Keep App Insights account context in sync when the active workspace changes.
    effect(() => {
      const user = this.workspaceService.currentUser();
      const workspace = this.workspaceService.activeWorkspace();
      if (user && workspace) {
        this.appInsights.setUserId(user.id, workspace.id);
      }
    });

    effect(() => {
      if (!this.isLoading() && this.isAuthenticated() && !this.userSynced) {
        this.userSynced = true;

        // Only block navigation (show spinner) when we have no local cache.
        // If workspaces are already in localStorage the user can proceed
        // immediately while the sync refreshes in the background.
        const hasLocalCache = this.workspaceService.workspaces().length > 0;
        if (!hasLocalCache) {
          this.syncInProgress.set(true);
        }

        this.apiService.syncUser().subscribe({
          next: ({ workspaces, profile }) => {
            if (workspaces.length === 0) {
              // Registered user but not associated with any workspace — treat as unauthorized.
              this.workspaceService.clearSession();
              this.syncInProgress.set(false);
              this.accessDenied.set(true);
              return;
            }
            this.workspaceService.cacheFromSync(workspaces, profile);
            const pendingWsId = this.pendingWorkspaceId;
            this.pendingWorkspaceId = null;
            if (pendingWsId) {
              const ws = workspaces.find((w) => w.id === pendingWsId);
              if (ws) this.workspaceService.setActiveWorkspace({ id: ws.id, name: ws.name ?? ws.id });
            }
            this.appInsights.setUserId(profile.id, this.workspaceService.activeWorkspace()?.id);
            this.syncInProgress.set(false);
            const target = this.pendingReturnUrl;
            this.pendingReturnUrl = null;
            if (target && target !== '/') this.router.navigateByUrl(target);
          },
          error: (err) => {
            // Any failure to sync the user (403, 500, network) means we cannot
            // trust the locally cached session — revoke everything immediately.
            this.workspaceService.clearSession();
            this.syncInProgress.set(false);
            if (err.status === 401) {
              this.authService.loginWithRedirect({ appState: { target: this.router.url } });
            } else if (err.status === 403) {
              this.accessDenied.set(true);
            }
          },
        });
      }
    });
  }

  login() {
    this.authService.loginWithRedirect({ appState: { target: this.router.url } });
  }

  switchWorkspace() {
    this.workspaceService.clearActiveWorkspace();
    this.router.navigate(['/select-workspace']);
  }

  logout() {
    this.appInsights.clearUserId();
    this.workspaceService.clearSession();
    this.authService.logout({ logoutParams: { returnTo: window.location.origin } });
  }
}
